<template>
  <div>
    <h2 class="mb-5">Computed vs Watched Property</h2>
    <v-row dense>
      <v-col cols="12" sm="6">
        <v-card light>
          <h3 class="pa-5">Computed</h3>
          <p class="ml-5 mr-5">
            We can think of a computed property as a normal property, but with
            the difference that Vue automatically evaluates its value every time
            one of its dependencies is changed. For instance:
          </p>
          <pre>
            <code>computed: {
  fullName() {
    return this.firstName + ' ' + this.lastName;
  }
}</code>
          </pre>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6">
        <v-card light>
          <h3 class="pa-5">Watch</h3>
          <p class="ml-5 mr-5">
            A watcher is basically a function that Vue automatically executes
            when the watched property changes. If we take the above example, we
            could do it as follows:
          </p>
          <pre>
            <code>watch: {
  firstName(val) {
    this.fullName = this.lastName + ' ' + val;
  },
  lastName(val) {
    this.fullName = this.firstName + ' ' + val;
  }
}</code>
          </pre>
        </v-card>
      </v-col>
    </v-row>

    <h3>
      While computed properties are more appropriate in most cases, watchers are
      more powerful and allow us to perform complex operations such as HTTP
      calls or execute some asynchronous logic that wouldn't be possible with a
      computed property.
    </h3>

    <pre>
      <code>&lt;template&gt;
  &lt;v-text-field v-model="question" label="Ask a question"&gt;&lt;/v-text-field&gt;
  &lt;v-row dense&gt;
    &lt;v-col align-self="start" class="pa-0" cols="12" v-if="image"&gt;
      &lt;v-avatar class="profile" color="grey" size="164" tile&gt;
        &lt;v-img :src=image&gt;&lt;/v-img&gt;
      &lt;/v-avatar&gt;
    &lt;/v-col&gt;
    &lt;v-col class="py-0"&gt;
      &lt;v-chip class="mt-2" label&gt;{{testString}}&lt;/v-chip&gt;
    &lt;/v-col&gt;
  &lt;/v-row&gt;
&lt;/template&gt;
&lt;script&gt;
  import axios from 'axios';
  export default {
    name: "ComputedWatcher",
    data() {
      return {
        question: '',
        answer: 'I cannot give you an answer until you ask a question!',
        image: '',
      };
    },
    created: function () {
      this.getAnswer();
    },
    watch: {
      // whenever question changes, this function will run
      question: function (newQuestion, oldQuestion) {
        this.answer = 'Waiting for you to stop typing...'
        this.getAnswer();
      }
    },
    methods: {
      async getAnswer () {
        if (this.question.indexOf('?') === -1) {
          this.answer = 'Questions usually contain a question mark. ;-)'
          return
        }
        this.answer = 'Thinking...'
        var vm = this;
        await axios({
            method: 'get',
            url: 'https://yesno.wtf/api',
        }).then(function (response) {
            vm.answer = response.data.answer;
            vm.image = response.data.image;
          })
          .catch(function (error) {
            vm.answer = 'Error! Could not reach the API. ' + error
          })
      }
    },
  }
&lt;/script&gt;</code>
    </pre>

    <h2>Output</h2>
    <br/>
    <v-card class="pa-5">
      <v-text-field v-model="question" label="Ask a question"></v-text-field>
      <v-row dense>
        <v-col align-self="start" class="pa-0" cols="12" v-if="image">
          <v-avatar class="profile" color="grey" size="164" tile>
            <v-img :src="image"></v-img>
          </v-avatar>
        </v-col>
        <v-col class="py-0">
          <v-chip class="mt-2" label>{{ answer.toUpperCase() }}</v-chip>
        </v-col>
      </v-row>
    </v-card>
<br/>
    <h2>Conclusion</h2>
    <br/>
    <p>
      Computed properties and watchers have different use cases. It would be a better idea to use a computed property whenever we need to calculate a value based on other properties, considering that Vue will reevaluate the property every time any of its dependencies is changed and we will not need a custom watcher for each dependency. But a watcher would be better if we need to do something more generic than just get a value, such as HTTP calls, store data in local storage, set a timer, or whatever we couldn't do with computed properties.
    </p>
  </div>
</template>
<script>
import axios from "axios";
export default {
  name: "ComputedWatcher",
  data() {
    return {
      testString: "{{answer.toUpperCase()}}",
      question: "",
      answer: "I cannot give you an answer until you ask a question!",
      image: "",
    };
  },
  created: function () {
    this.getAnswer();
  },
  watch: {
    // whenever question changes, this function will run
    question: function (newQuestion, oldQuestion) {
      this.answer = "Waiting for you to stop typing...";
      this.getAnswer();
    },
  },
  methods: {
    async getAnswer() {
      if (this.question.indexOf("?") === -1) {
        this.answer = "Questions usually contain a question mark. ;-)";
        return;
      }
      this.answer = "Thinking...";
      var vm = this;
      await axios({
        method: "get",
        url: "https://yesno.wtf/api",
      })
        .then(function (response) {
          vm.answer = response.data.answer;
          vm.image = response.data.image;
        })
        .catch(function (error) {
          vm.answer = "Error! Could not reach the API. " + error;
        });
    },
  },
};
</script>
